<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useField, useForm } from 'vee-validate'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DMultiselect,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon,
  Lock03Icon,
  LockUnlocked03Icon
} from 'front-library'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
import {
  getPayrollConcepts,
  PayrollConcepts,
  deletePayrollConcept,
  updateStatePayrollConcept,
  getListMasterConceptsGroups
} from '@/app/core/services/payroll-concepts'
import { usePayrollStore } from '@/store/payroll-data'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const storePayroll = usePayrollStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref()
const typeAlertModal = ref('')
const acceptQuestion = ref()

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()

const itemsMasterConceptGroups = ref([] as any)
const { value: allowedGroupsConcepts } = useField<string[]>(ref('allowedGroupsConcepts'))
const searchGroups = ref('')

const ListPayrollConcepts = ref(null)
const payrollConceptForDelete = ref()
const payrollConceptUpdateState = ref()
const isDelete = ref(false)
const urlFile = ref(null)
const refresh = ref()
const payload = ref()

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('payrollConcepts.list.code'), table: 'code' },
  { name: 'name', title: t('payrollConcepts.list.name'), table: 'name' },
  {
    name: 'masterConcept',
    title: t('payrollConcepts.list.masterConcept'),
    table: 'master_concepts.name'
  },
  {
    name: 'creditAccountId',
    title: t('payrollConcepts.list.creditAccount'),
    table: 'concepts.credit_account_id'
  },
  {
    name: 'debitAccountId',
    title: t('payrollConcepts.list.debitAccount'),
    table: 'concepts.debit_account_id'
  },
  { name: 'groups', title: t('masterConcepts.list.groups'), table: 'groups' },
  {
    name: 'format',
    title: t('payrollConcepts.list.format'),
    table: 'format'
  },
  ...(storePayroll.payroll?.country?.iso_code === 'UY'
    ? [
        {
          name: 'uycodeagrupationbps',
          title: t('payrollConcepts.list.uyCodeAgrupationBPS'),
          table: 'uycodeagrupationbps'
        },
        {
          name: 'uycodegroupprefacturebps',
          title: t('payrollConcepts.list.uyCodeGroupPrefactureBPS'),
          table: 'uycodegroupprefacturebps'
        }
      ]
    : []),
  ...(storePayroll.payroll?.country?.iso_code === 'MX'
    ? [
        {
          name: 'fiscalrule',
          title: 'Regla de Timbrado',
          table: 'fiscalrule'
        },
        {
          name: 'taxedconcept',
          title: 'Concepto Gravado',
          table: 'taxedconcept'
        },
        {
          name: 'exemptconcept',
          title: 'Concepto Exento',
          table: 'exemptconcept'
        },
      ]
    : []),
  {
    name: 'state',
    title: t('payrollConcepts.list.status'),
    table: 'is_inactive'
  },
  {
    name: 'updated_at',
    title: t('payrollConcepts.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<PayrollConcepts[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createPayrollConcept = () => {
  void router.push({ name: 'CreatePayrollConcept' })
}
const updatePayrollConcept = (id: any) => {
  void router.push({ name: 'UpdatePayrollConcept', params: { id } })
}
const getPayrollConcept = (id: any) => {
  void router.push({ name: 'GetPayrollConcept', params: { id } })
}

/* Cambiar estado del concepto de la nomina */
const changeStatePayrollConcept = (values: any) => {
  const text = values.is_inactive
    ? t('table.action.activate')
    : t('table.action.deactivate')
  titleModal.value = t('payrollConcepts.list.actionStatus', {
    status: text,
    payrollConcept: values.name as string
  })
  payrollConceptUpdateState.value = [
    { name: values.name, id: values.id, is_inactive: values.is_inactive }
  ]
  descriptionModal.value = ''
  typeAlertModal.value = 'question'
  acceptQuestion.value = t('modal.acceptQuestion', { action: text })
  showModal.value = true
  isDelete.value = false
}
const changeStateConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await updateStatePayrollConcept(
    payrollConceptUpdateState.value[0].id,
    payrollConceptUpdateState.value[0].is_inactive
  )
    .then(response => {
      notifications.addNotification({
        title: !payrollConceptUpdateState.value[0].is_inactive
          ? t('notification.disabled')
          : t('notification.activated'),
        text: t('payrollConcepts.notification.changeStatus', {
          payrollConcept: payrollConceptUpdateState.value[0].name as string,
          status: !payrollConceptUpdateState.value[0].is_inactive
            ? t('table.action.deactivated')
            : t('table.action.asset')
        }),
        type: 'success',
        time: 10000
      })
      void payrollConcepts()
    })
    .catch((error: any) => {
      notificationError(error, 'payrollConcepts', t, notifications, loading)
    })
}

/* Eliminar concepto de la nomina */
const removePayrollConcept = (values: any) => {
  typeAlertModal.value = 'question'
  titleModal.value = t('payrollConcepts.list.actionStatus', {
    status: t('table.action.delete'),
    payrollConcept: values.name as string
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  showModal.value = true
  payrollConceptForDelete.value = values.id
  isDelete.value = true
}
const deleteConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await deletePayrollConcept(payrollConceptForDelete.value)
    .then(response => {
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('payrollConcepts.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void payrollConcepts()
    })
    .catch((error: any) => {
      notificationError(error, 'payrollConcepts', t, notifications, loading)
    })
}

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: payload.value.fields.filter(
          (field: any) => !field.includes('.id') && !field.includes('as applyGroupMemberships')
        ),
        headers: columns.value
          .filter((item: any) => item.title !== '')
          .map((item: any) => item.title),
        module: t('payrollConcepts.list.title', 1).replace('ó', 'o')
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'payrollConcepts', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  urlFile.value = null
}

/* Listar conceptos de la nomina */
const payrollConcepts = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getPayrollConcepts(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value,
      storePayroll.payroll?.country?.iso_code || undefined,
      allowedGroupsConcepts.value?.length ? allowedGroupsConcepts.value : undefined
    )
    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    payload.value = data[1]
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'payrollConcepts', t, notifications, loading)
  }
}

onMounted(async() => {
  itemsMasterConceptGroups.value = await getListMasterConceptsGroups()
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="isDelete ? deleteConfirm() : changeStateConfirm()">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('payrollConcepts.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('payrollConcepts.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 flex-auto flex-wrap gap-2 items-end">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-96"
            v-model="search"
            @keyup.enter="payrollConcepts" />
          <div class="col-span-10 sm:col-span-5 md:col-span-2">            
            <DMultiselect           
              v-model="allowedGroupsConcepts"
              :label="t('payrollConcepts.list.groups')"
              :headerTitle="t('payrollConcepts.list.groups')"
              :options="itemsMasterConceptGroups" />
          </div>
          <DBtn
            @click.prevent="payrollConcepts"
            v-html="t('form.button.search')" />
        </div>
        <div class="flex gap-2 items-end justify-end">
          <DBtn
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
          <DBtn
            :icon="'PlusIcon'"
            type="button"
            @click="createPayrollConcept()"
            v-if="store.hasPermission('concept:create')">
            {{
              t('form.button.create', {
                form: t('payrollConcepts.list.title', 0)
              })
            }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="ListPayrollConcepts"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), payrollConcepts()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), payrollConcepts()">
      <template #groups="data: any">        
        <div v-if="data.applyGroupMemberships" class="p-2 text-gray-500 text-sm ">
         <ul>
          <li v-for="(group,index) in data.groups" :key="index"  class="list-disc marker:text-amber-500">{{ group }}</li>
         </ul>
        </div>
        <div v-else class="p-2 text-gray-500 text-sm ">
         <ul>
          <li v-for="(group,index) in data.groups" :key="index"  class="list-disc">{{ group }}</li>
         </ul>
        </div>
      </template>
      <template #state="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.is_inactive
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="data.is_inactive ? 'bg-red-600' : 'bg-green-600'"></div>
          {{
            data.is_inactive
              ? t('table.action.inactive')
              : t('table.action.active')
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('concept:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getPayrollConcept(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="!data.is_inactive && store.hasPermission('concept:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updatePayrollConcept(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('concept:enable')"
            arrow
            offsetDistance="12"
            :content="
              data.is_inactive
                ? t('payrollConcepts.list.activate')
                : t('payrollConcepts.list.deactivate')
            "
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeStatePayrollConcept(data)">
              <Lock03Icon
                v-if="data.is_inactive"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              <LockUnlocked03Icon
                v-else
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="data.is_inactive && store.hasPermission('concept:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removePayrollConcept(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
